import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Banner, Spinner } from "@panelgenie/shared-ui";
import { InvocationDetailView } from "../../components/InvocationDetailView.js";

/**
 * The detail surface is a full-viewport three-pane console that owns its
 * own top bar (with the back affordance), so this route renders it
 * full-bleed. Only the load / error envelope keeps the constrained,
 * padded layout the rest of the console uses.
 */

/**
 * Detail view — one invocation by id from `support.getInvocation`.
 *
 * The fetch is the privacy-sensitive call: the server audits it (S-04)
 * and returns presigned photo GET URLs alongside the raw response and
 * the recomputed parse result. The component renders all three; this
 * route just handles the load / error envelope and the back link.
 */
export const Route = createFileRoute("/invocations/$id")({
  component: InvocationDetailPage,
});

function InvocationDetailPage() {
  const { id } = Route.useParams();
  const { trpc } = Route.useRouteContext();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["invocations", "detail", id],
    queryFn: () => trpc.support.getInvocation.query({ id }),
  });

  if (query.isError) {
    return (
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-5 overflow-y-auto px-4 py-6">
        <Link to="/" className="text-sm text-accent hover:underline">
          ← Back to invocations
        </Link>
        <Banner variant="danger" title="Couldn't load this invocation">
          {query.error instanceof Error
            ? query.error.message
            : "Request failed. The invocation may not exist, or staff access is required."}
        </Banner>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center gap-3 text-mute">
        <Spinner size="md" />
        <span>Loading invocation…</span>
      </div>
    );
  }

  return (
    <InvocationDetailView
      detail={query.data}
      saveTestCase={(input) => trpc.support.createTestCase.mutate(input)}
      onSavedTestCase={(testCaseId) => {
        // TC-7 landed the `/test-cases/$id` route, so this is now a typed
        // navigation to the freshly-saved case's detail page.
        void navigate({
          to: "/test-cases/$id",
          params: { id: testCaseId },
        });
      }}
    />
  );
}
